import axios from "axios";
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';
import { downloadBlobResponse } from '@iso/lib/helpers/fileDownload';

export const incidentService = {
  getList,
  getById,
  createOrUpdate,
  getHistories,
  getActivities,
  createActivity,
  getSettings,
  saveSettings,
  classify,
  reclassify,
  notify,
  getNotificationRules,
  closeIncident,
  deleteIncident,
  getInvestigation,
  submitInvestigation,
  approveInvestigation,
  requestMoreInfo,
  editInvestigation,
  unlockInvestigationMetadata,
  getCorrectiveActions,
  createCorrectiveAction,
  completeCorrectiveAction,
  reallocateCorrectiveAction,
  getLessonsLearned,
  generateLessonsLearned,
  updateLessonsLearned,
  distributeLessonsLearned,
  toggleLessonsLearnedRequired,
  exportIncidents,
};

function getList(params){
  return (
    axios
      .get(
        apiEndpoint.incidents,
        {
          params,
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function getById(id) {
  return axios
    .get(`${apiEndpoint.incidents}/${id}`, {
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function createOrUpdate(data, id = null){
  return (id ? (
    axios
      .put(
        apiEndpoint.incidents + '/' + id,
        data,
        {
          headers: authHeader(),
        }
      )
  ) : (
    axios
      .post(
        apiEndpoint.incidents,
        data,
        {
          headers: authHeader()
        }
      )
  )).then(res => {
    if(res && res.status === 200){
      return res.data;
    }
  });
}

function getHistories(params){
  return (
    axios
      .get(
        apiEndpoint.incidentHistories,
        {
          params,
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function getActivities(params){
  return (
    axios
      .get(
        apiEndpoint.incidentActivities,
        {
          params,
          headers: authHeader(),
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function createActivity(data){
  return (
    axios
      .post(
        apiEndpoint.incidentActivities,
        data,
        {
          headers: authHeader()
        }
      ).then(res => {
        if(res && res.status === 200){
          return res.data;
        }
      })
  );
}

function getSettings(propertyId) {
  return axios
    .get(`${apiEndpoint.incidents}/settings`, {
      params: { property_id: propertyId },
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function saveSettings(propertyId, data) {
  return axios
    .post(
      `${apiEndpoint.incidents}/save-settings`,
      { ...data, property_id: propertyId },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

// --- Safety Gate ---

function classify(id, data) {
  return axios
    .post(
      `${apiEndpoint.incidents}/${id}/classify`,
      data,
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function reclassify(id, data) {
  return axios
    .post(
      `${apiEndpoint.incidents}/${id}/reclassify`,
      data,
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function notify(id, ruleCode) {
  return axios
    .post(
      `${apiEndpoint.incidents}/${id}/notify`,
      { rule_code: ruleCode },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function getNotificationRules(propertyId) {
  return axios
    .get(`${apiEndpoint.incidents}/notification-rules`, {
      params: { property_id: propertyId },
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function closeIncident(id) {
  return axios
    .post(
      `${apiEndpoint.incidents}/${id}/close`,
      {},
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function deleteIncident(id, confirmReportNumber) {
  return axios
    .delete(`${apiEndpoint.incidents}/${id}`, {
      headers: authHeader(),
      data: { confirm_report_number: confirmReportNumber },
    })
    .then((res) => (res && res.status === 200 ? res.data : null));
}

// --- Investigation ---

function getInvestigation(incidentId) {
  return axios
    .get(`${apiEndpoint.incidentInvestigations}/view`, {
      params: { incident_id: incidentId },
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : null))
    .catch(() => null);
}

function submitInvestigation(incidentId, data) {
  return axios
    .post(
      `${apiEndpoint.incidentInvestigations}/submit`,
      { incident_id: incidentId, ...data },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function approveInvestigation(incidentId, comment, correctiveActions) {
  return axios
    .post(
      `${apiEndpoint.incidentInvestigations}/approve`,
      {
        incident_id: incidentId,
        comment,
        corrective_actions: correctiveActions || [],
      },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function requestMoreInfo(incidentId, comment) {
  return axios
    .post(
      `${apiEndpoint.incidentInvestigations}/request-more-info`,
      { incident_id: incidentId, comment },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function editInvestigation(incidentId, data) {
  return axios
    .put(
      `${apiEndpoint.incidentInvestigations}/edit`,
      { incident_id: incidentId, ...data },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

// Toggle the Factual Investigation incidentMetadata lock. Defaults to unlocking
// the section so the investigator can amend an auto-populated field; passing
// `locked: true` re-locks it after the edit. Safety Admin only.
function unlockInvestigationMetadata(incidentId, locked = false) {
  return axios
    .post(
      `${apiEndpoint.incidentInvestigations}/unlock-metadata`,
      { incident_id: incidentId, locked },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

// --- Corrective Actions ---

function getCorrectiveActions(incidentId) {
  return axios
    .get(apiEndpoint.incidentCorrectiveActions, {
      params: { incident_id: incidentId },
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : []))
    .catch(() => []);
}

function createCorrectiveAction(data) {
  return axios
    .post(
      apiEndpoint.incidentCorrectiveActions,
      data,
      { headers: authHeader() }
    )
    .then((res) => (res && (res.status === 200 || res.status === 201) ? res.data : null));
}

function completeCorrectiveAction(actionId, evidence, evidenceAttachmentIds = []) {
  return axios
    .post(
      `${apiEndpoint.incidentCorrectiveActions}/${actionId}/complete`,
      { evidence, evidence_attachment_ids: evidenceAttachmentIds },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function reallocateCorrectiveAction(actionId, data) {
  return axios
    .post(
      `${apiEndpoint.incidentCorrectiveActions}/${actionId}/reallocate`,
      data,
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

// --- Lessons Learned ---

function getLessonsLearned(incidentId) {
  return axios
    .get(`${apiEndpoint.incidentLessonsLearned}/view`, {
      params: { incident_id: incidentId },
      headers: authHeader(),
    })
    .then((res) => (res && res.status === 200 ? res.data : null))
    .catch(() => null);
}

function generateLessonsLearned(incidentId) {
  return axios
    .post(
      `${apiEndpoint.incidentLessonsLearned}/generate`,
      { incident_id: incidentId },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function updateLessonsLearned(incidentId, content) {
  return axios
    .put(
      `${apiEndpoint.incidentLessonsLearned}/update`,
      { incident_id: incidentId, content },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function distributeLessonsLearned(incidentId) {
  return axios
    .post(
      `${apiEndpoint.incidentLessonsLearned}/distribute`,
      { incident_id: incidentId },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

function toggleLessonsLearnedRequired(incidentId, required) {
  return axios
    .post(
      `${apiEndpoint.incidentLessonsLearned}/toggle-required`,
      { incident_id: incidentId, required },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : null));
}

/**
 * Download the property's incidents as a CSV. Admin-only server side — a
 * non-admin caller gets a 403 back rather than a file.
 *
 * @param {Object} params property_id plus the list screen's optional search /
 *   type / status filters, so the file mirrors what is on screen. Pagination is
 *   deliberately not sent: an export carries every matching row.
 * @returns {Promise<void>}
 */
function exportIncidents(params) {
  return axios
    .get(`${apiEndpoint.incidents}/export`, {
      params,
      headers: { ...authHeader(), Accept: 'text/csv' },
      responseType: 'blob',
    })
    .then((res) => {
      downloadBlobResponse(res, `incidents-${params?.property_id || 'export'}.csv`);
    });
}
